import React, { useState, useEffect } from 'react';
import './BurgerMenu.css';

/**
 * Menu items constant - defined outside component since it never changes
 * @constant
 * @type {Array<{id: string, label: string}>}
 */
const menuItems = [
  { id: 'hero', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'approach', label: 'Our Approach' },
  { id: 'menu', label: 'Menu' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'location', label: 'Location' }
];

/**
 * BurgerMenu Component
 *
 * Responsive navigation menu with hamburger icon that slides in from the right.
 * Features smooth scrolling to sections, active section highlighting, and keyboard navigation.
 *
 * @component
 * @returns {JSX.Element} Burger menu with overlay and navigation links
 *
 * @example
 * return (
 *   <BurgerMenu />
 * )
 */
function BurgerMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest('.burger-menu') && !e.target.closest('.burger-icon')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Track active section on scroll
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    menuItems.forEach(item => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  // Prevent scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const handleNavClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsOpen(false);
  };

  return (
    <>
      {/* Burger Icon */}
      <button
        className={`burger-icon ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
        aria-expanded={isOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Backdrop */}
      <div
        className={`burger-backdrop ${isOpen ? 'visible' : ''}`}
        onClick={() => setIsOpen(false)}
      />

      {/* Menu Panel */}
      <nav className={`burger-menu ${isOpen ? 'open' : ''}`} role="navigation" aria-label="Main navigation">
        <div className="burger-menu-header">
          <h3>Navigation</h3>
        </div>

        <ul className="burger-menu-items">
          {menuItems.map((item, index) => (
            <li
              key={item.id}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <button
                className={`burger-menu-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item.id)}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="burger-menu-footer">
          <a
            href="https://ordercoffee.uk"
            className="burger-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Order Online
          </a>
        </div>
      </nav>
    </>
  );
}

export default BurgerMenu;
